import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewEncapsulation, inject, signal } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { PendenciesService } from '../../core/api/pendencies/pendencies.service'
import { UsersService } from '../../core/api/users/users.service'
import { ConfirmDialogService } from '../../core/services/confirm-dialog/confirm-dialog.service'
import { FcSelectMultiplePagedComponent } from '../fc-select-multiple-paged/fc-select-multiple-paged.component'

@Component({
    selector: 'app-update-pendency-assignee-modal',
    templateUrl: './update-pendency-assignee-modal.component.html',
    styleUrls: ['./update-pendency-assignee-modal.component.scss', '../../../styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [
        CommonModule,
        MatIconModule,
        TranslateModule,
        FcSelectMultiplePagedComponent,
        MatProgressBarModule,
    ],
    providers: [
        ConfirmDialogService
    ]
})
export class UpdatePendencyAssigneeModalComponent implements OnChanges, OnDestroy {

  @Input() public assignees: any[] = []
  @Input() public pendencyId: any = null
  @Input() public mode: 'assignees' | 'readers' = 'assignees'
  @Input() public ignoredIds: string[] = []

  public readonly usersService = inject(UsersService)
  private readonly pendenciesService = inject(PendenciesService)
  private readonly confirmDialogService = inject(ConfirmDialogService)
  private readonly translateService = inject(TranslateService)

  public isUpdatingAssignee = signal(false)
  public shouldDisableButton = signal(true)
  public isLoading = signal(false)
  public assigneeForm = new FormGroup({
    assignees: new FormControl(this.assignees)
  })
  private subscription!: Subscription | undefined

  @Output() public readonly closeDialogEvent = new EventEmitter<any>()
  @Output() public readonly showDialogEvent = new EventEmitter<any>()
  @Output() public readonly hideDialogEvent = new EventEmitter<any>()

  public ngOnChanges(changes: any): void {
    if (changes.pendencyId) {
      this.pendencyId = changes.pendencyId.currentValue
    }

    if (changes.mode) {
      this.mode = changes.mode.currentValue
    }

    if (changes.ignoredIds) {
      this.ignoredIds = changes.ignoredIds.currentValue || []
    }

    if (changes.assignees) {
      this.assignees = changes.assignees.currentValue || []
      this.isUpdatingAssignee.set(this.assignees.length > 0)
      this.assigneeForm.get('assignees')?.setValue(this.assignees)
    }

    if (!this.subscription) {
      this.subscription = this.assigneeForm.get('assignees')?.valueChanges.subscribe((assignees: any[] | null) => {
        const currentIds = (this.assignees || []).map((a: any) => a?.id).sort((a, b) => a.localeCompare(b)).join(',')
        const newIds = (assignees || []).map((a: any) => a?.id).sort((a, b) => a.localeCompare(b)).join(',')
        const assigneesHaveChanged = currentIds !== newIds

        if ((!this.isUpdatingAssignee() && (!assignees || assignees.length === 0)) || (this.isUpdatingAssignee() && !assigneesHaveChanged)) {
          this.shouldDisableButton.set(true)
          return
        }

        this.shouldDisableButton.set(false)
      })
    }

    this.assigneeForm.get('assignees')?.updateValueAndValidity()
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  public closeModal(): void {
    this.closeDialogEvent.emit()
  }

  public async updateAssignee(): Promise<void> {
    try {
      const assignees: any[] = this.assigneeForm.get('assignees')?.value || []

      if (assignees.length === 0 && this.isUpdatingAssignee()) {
        this.hideDialogEvent.emit()
        await this.openRemoveAssigneeConfirmDialog()
        return
      }

      this.isLoading.set(true)
      const payload: any = { id: this.pendencyId }
      if (this.mode === 'assignees') {
        payload.assigneeIds = assignees.map(a => a.id)
      } else {
        payload.readerIds = assignees.map(a => a.id)
      }

      const { data } = await this.pendenciesService.updateAssignee(payload)

      if (this.isUpdatingAssignee()) {
        this.closeDialogEvent.emit({ pendency: data.updatePendencyAssignee, message: 'SUCCESS_UPDATE' })
      } else {
        this.closeDialogEvent.emit({ pendency: data.updatePendencyAssignee, message: 'SUCCESS' })
      }

      this.isLoading.set(false)
    } catch {
      const errorMode = this.mode === 'assignees' ? 'ASSIGNEE' : 'READER'
      this.closeDialogEvent.emit({ error: `UPDATE_${errorMode}_ERROR`, message: 'ERROR' })
      this.isLoading.set(false)
    }
  }

  public get translationPrefix(): string {
    return this.mode === 'assignees' ? 'UPDATE_PENDENCY_ASSIGNEE' : 'UPDATE_PENDENCY_READER'
  }

  private async openRemoveAssigneeConfirmDialog(): Promise<void> {
    const prefix = this.translationPrefix
    const title = this.translateService.instant(`${prefix}.REMOVE_ASSIGNEE_MODAL.TITLE`)
    const message = this.translateService.instant(`${prefix}.REMOVE_ASSIGNEE_MODAL.MESSAGE`)

    await this.confirmDialogService.open({
      title,
      message,
      buttons: [
        {
          text: this.translateService.instant('CONFIRM_DIALOG.YES'),
          exec: async () => {

            try {
              this.isLoading.set(true)
              const payload: any = { id: this.pendencyId }
              if (this.mode === 'assignees') {
                payload.assigneeIds = []
              } else {
                payload.readerIds = []
              }

              const { data } = await this.pendenciesService.updateAssignee(payload)
              this.isLoading.set(false)
              this.closeDialogEvent.emit({ pendency: data.updatePendencyAssignee, message: 'SUCCESS' })
            } catch {
              const errorMode = this.mode === 'assignees' ? 'ASSIGNEE' : 'READER'
              this.closeDialogEvent.emit({ error: `REMOVE_${errorMode}_ERROR`, message: 'ERROR' })
              this.isLoading.set(false)
            }
          }
        },
        {
          text: this.translateService.instant('CONFIRM_DIALOG.NO'),
          primary: true,
          exec: () => {
            this.showDialogEvent.emit()
          }
        }
      ]
    })
  }
}
